import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadioCards, Flex, Text, Box, TextField, Spinner, Tooltip, Dialog, Button } from '@radix-ui/themes';
import Icon from '@mdi/react';
import { mdiHelpCircle, mdiChevronLeft } from '@mdi/js';
import { useStore } from '../store/store';
import helpImage from '../assets/model_name_help.jpg';

// Model definitions
const proseModels = [
    {
        id: 'anthropic/claude-3.7-sonnet:thinking',
        name: 'Claude 3.7',
        description: 'Best-in-class prose quality for a reasonable price.'
    },
    {
        id: 'deepseek/deepseek-r1',
        name: 'Deepseek R1',
        description: 'Great value regarding quality and price. NSFW capeable.'
    }
];

const chatModels = [
    // {
    //     id: 'anthropic/claude-3.7-sonnet:thinking',
    //     name: 'Claude 3.7',
    //     description: 'Great conversational abilities and reasoning.'
    // },
    {
        id: 'deepseek/deepseek-r1',
        name: 'Deepseek R1',
        description: 'Cost-effective with excelent knowledge capabilities.'
    },
    {
        id: 'google/gemini-2.0-flash-001',
        name: 'Gemini 2.0 Flash',
        description: 'Quick response (non-thinking) and low cost.'
    }
];

const minorModels = [
    {
        id: 'meta-llama/llama-3.1-405b-instruct',
        name: 'Llama 3.1',
        description: 'Most efficient option for minor tasks.'
    },
    {
        id: 'google/gemini-2.0-flash-001',
        name: 'Gemini 2.0 Flash',
        description: 'High quality and still cheap.'
    }
];

export const ModelSettingsScreen = () => {
    const navigate = useNavigate();
    const [state, actions] = useStore();
    const [proseModel, setProseModel] = useState(proseModels[0].id);
    const [chatModel, setChatModel] = useState(chatModels[0].id);
    const [minorModel, setMinorModel] = useState(minorModels[0].id);
    const [showCustomProseModelInput, setShowCustomProseModelInput] = useState(false);
    const [showCustomChatModelInput, setShowCustomChatModelInput] = useState(false);
    const [showCustomMinorModelInput, setShowCustomMinorModelInput] = useState(false);
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);

    // Load initial values from user settings
    useEffect(() => {
        if (state.user?.settings) {
            try {
                const userSettings = JSON.parse(state.user.settings);
                if (userSettings.models) {
                    console.log('Setting Models...');

                    setProseModel(userSettings.models.prose || proseModels[0].id);
                    setChatModel(userSettings.models.chat || chatModels[0].id);
                    setMinorModel(userSettings.models.minor || minorModels[0].id);
                    
                    // Set custom input states based on default model values
                    setShowCustomProseModelInput(!proseModels.some(model => model.id === userSettings.models.prose));
                    setShowCustomChatModelInput(!chatModels.some(model => model.id === userSettings.models.chat));
                    setShowCustomMinorModelInput(!minorModels.some(model => model.id === userSettings.models.minor));
                }
            } catch (error) {
                console.error('Error parsing user settings:', error);
            }
        }
    }, []);

    // Update settings when any model changes
    useEffect(() => {
        const userSettings = JSON.parse(state.user.settings || '{"models":{}}');
        userSettings.models = {
            prose: proseModel,
            chat: chatModel,
            minor: minorModel
        };
        
        // Update the settings in the state
        actions.updateUserSettings(JSON.stringify(userSettings));
        
        // Also update the user in localStorage
        const updatedUser = {
            ...state.user,
            settings: JSON.stringify(userSettings)
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
    }, [proseModel, chatModel, minorModel]);

    return (
        <div className="settings-container">
            <Flex onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} >
                <Icon path={mdiChevronLeft} size={1} />
                <div>Back</div>
            </Flex>
            <h2>Model Settings</h2>
            {/* Prose Model Section */}
            <div className="model-settings">
                <div className="model-settings-type">Prose Model</div>
                <div className="model-settings-explainer">This model will be used to generate all the actual prose for the novel</div>
                <Box maxWidth="600px">
                    <RadioCards.Root value={proseModel} columns={{ initial: "1", sm: "3" }}>
                        {proseModels.map(model => (
                            <RadioCards.Item 
                                key={model.id}
                                value={model.id}
                                onClick={() => {
                                    setShowCustomProseModelInput(false);
                                    setProseModel(model.id);
                                }}
                            >
                                <Flex direction="column" width="100%">
                                    <Text weight="bold">{model.name}</Text>
                                    <Text>{model.description}</Text>
                                </Flex>
                            </RadioCards.Item>
                        ))}
                        <RadioCards.Item 
                            value="custom"
                            onClick={() => {
                                setShowCustomProseModelInput(true);
                                if (!showCustomProseModelInput && !proseModel.includes('/')) {
                                    setProseModel(''); // Clear model when switching to custom
                                }
                            }}
                        >
                            <Flex direction="column" width="100%">
                                <Text weight="bold">Custom</Text>
                                <Text>Use a model of your choice from OpenRouter</Text>
                            </Flex>
                        </RadioCards.Item>
                    </RadioCards.Root>
                    <br />
                    {showCustomProseModelInput &&
                    <Flex>
                        <TextField.Root
                            style={{width: '85%'}}
                            placeholder="Paste the model name from OpenRouter here"
                            value={proseModel}
                            onChange={(e) => setProseModel(e.target.value)}
                        />
                        <Icon 
                            path={mdiHelpCircle} 
                            size={2} 
                            className="custom-model-help"
                            onClick={() => setHelpDialogOpen(true)} 
                        />
                    </Flex>
                    }
                </Box>
            </div>

            {/* Chat Model Section */}
            <div className="model-settings">
                <div className="model-settings-type">Chat Model</div>
                <div className="model-settings-explainer">This model will be used for the chat interface when discussing your novel</div>
                <Box maxWidth="600px">
                    <RadioCards.Root value={chatModel} columns={{ initial: "1", sm: "3" }}>
                        {chatModels.map(model => (
                            <RadioCards.Item 
                                key={model.id}
                                value={model.id}
                                onClick={() => {
                                    setShowCustomChatModelInput(false);
                                    setChatModel(model.id);
                                }}
                            >
                                <Flex direction="column" width="100%">
                                    <Text weight="bold">{model.name}</Text>
                                    <Text>{model.description}</Text>
                                </Flex>
                            </RadioCards.Item>
                        ))}
                        <RadioCards.Item 
                            value="custom"
                            onClick={() => {
                                setShowCustomChatModelInput(true);
                                if (!showCustomChatModelInput && !chatModel.includes('/')) {
                                    setChatModel(''); // Clear model when switching to custom
                                }
                            }}
                        >
                            <Flex direction="column" width="100%">
                                <Text weight="bold">Custom</Text>
                                <Text>Use a model of your choice from OpenRouter</Text>
                            </Flex>
                        </RadioCards.Item>
                    </RadioCards.Root>
                    <br />
                    {showCustomChatModelInput &&
                    <Flex>
                        <TextField.Root
                            style={{width: '85%'}}
                            placeholder="Paste the model name from OpenRouter here"
                            value={chatModel}
                            onChange={(e) => setChatModel(e.target.value)}
                        />
                        <Icon 
                            path={mdiHelpCircle} 
                            size={2} 
                            className="custom-model-help"
                            onClick={() => setHelpDialogOpen(true)} 
                        />
                    </Flex>
                    }
                </Box>
            </div>

            {/* Minor Tasks Model Section */}
            <div className="model-settings">
                <div className="model-settings-type">Minor Tasks Model</div>
                <div className="model-settings-explainer">This model will be used for less critical tasks like generating summaries and analyses</div>
                <Box maxWidth="600px">
                    <RadioCards.Root value={minorModel} columns={{ initial: "1", sm: "3" }}>
                        {minorModels.map(model => (
                            <RadioCards.Item 
                                key={model.id}
                                value={model.id}
                                onClick={() => {
                                    setShowCustomMinorModelInput(false);
                                    setMinorModel(model.id);
                                }}
                            >
                                <Flex direction="column" width="100%">
                                    <Text weight="bold">{model.name}</Text>
                                    <Text>{model.description}</Text>
                                </Flex>
                            </RadioCards.Item>
                        ))}
                        <RadioCards.Item 
                            value="custom"
                            onClick={() => {
                                setShowCustomMinorModelInput(true);
                                if (!showCustomMinorModelInput && !minorModel.includes('/')) {
                                    setMinorModel(''); // Clear model when switching to custom
                                }
                            }}
                        >
                            <Flex direction="column" width="100%">
                                <Text weight="bold">Custom</Text>
                                <Text>Use a model of your choice from OpenRouter</Text>
                            </Flex>
                        </RadioCards.Item>
                    </RadioCards.Root>
                    <br />
                    {showCustomMinorModelInput &&
                    <Flex>
                        <TextField.Root
                            style={{width: '85%'}}
                            placeholder="Paste the model name from OpenRouter here"
                            value={minorModel}
                            onChange={(e) => setMinorModel(e.target.value)}
                        />
                        <Icon 
                            path={mdiHelpCircle} 
                            size={2} 
                            className="custom-model-help"
                            onClick={() => setHelpDialogOpen(true)} 
                        />
                    </Flex>
                    }
                </Box>
            </div>

            {/* Help Dialog */}
            <Dialog.Root open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
                <Dialog.Content style={{ maxWidth: '600px' }}>
                    <Dialog.Title>Custom Model Selection</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        To use a custom model, you'll need to get the exact model name from OpenRouter. 
                        Navigate to OpenRouter, select a model, and copy its identifier as shown below.
                    </Dialog.Description>

                    <Box my="4">
                        <img 
                            src={helpImage}
                            alt="How to find model names on OpenRouter" 
                            style={{ width: '100%', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)' }} 
                        />
                    </Box>

                    <Text as="p" size="2" mb="4">
                        Paste the complete model identifier (including any version or parameter tags) into the text field.
                        For example: <code>anthropic/claude-3-opus:beta</code> or <code>mistralai/mixtral-8x7b-instruct</code>
                    </Text>
                    
                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" style={{cursor: 'pointer'}}>Got it</Button>
                        </Dialog.Close>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </div>
    );
};

export default ModelSettingsScreen;